/* ============================================================
 * Lion Theme — lion-stock-notifier.js
 *
 * <lion-stock-notifier> — Back-in-stock email capture.
 * Intercepts the contact form submit, posts via fetch to the
 * Shopify /contact endpoint, and shows an inline success message.
 * Falls back to native form submission if fetch fails.
 *
 * Theme Store compliant: no external endpoint, no third-party SDK.
 * Merchants can plug a Back-in-Stock app via the @app block slot
 * inside main-product (this fallback is opt-out via section setting).
 * ============================================================ */

class StockNotifier extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.submitBtn = this.querySelector('[type="submit"]');
    this.label = this.querySelector('[data-stock-notifier-label]');
    this.successEl = this.querySelector('[data-stock-notifier-success]');
    this.errorEl = this.querySelector('[data-stock-notifier-error]');
    this.emailInput = this.querySelector('input[type="email"]');

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    /* React to variant changes so the form's hidden Variant fields
       always reflect the current selection, and toggle visibility. */
    if (window.subscribe && window.PUB_SUB_EVENTS?.variantChange) {
      this.variantUnsub = window.subscribe(window.PUB_SUB_EVENTS.variantChange, (data) => {
        if (data?.variant) {
          this.syncVariant(data.variant);
        } else {
          this.hidden = true;
        }
      });
    }
  }

  disconnectedCallback() {
    this.variantUnsub?.();
  }

  syncVariant(variant) {
    const setHidden = (name, value) => {
      const input = this.form?.querySelector(`input[name="contact[${name}]"]`);
      if (input) input.value = value ?? '';
    };
    setHidden('Variant', variant.title);
    setHidden('Variant ID', variant.id);
    setHidden('SKU', variant.sku);
    this.dataset.variantId = variant.id;

    /* Show notifier only when the selected variant is unavailable */
    if (variant.available) {
      this.hidden = true;
      this.querySelector('details')?.removeAttribute('open');
    } else {
      this.hidden = false;
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    if (!this.form || !this.emailInput) return;

    const email = this.emailInput.value.trim();
    if (!email) return;

    this.setLoading(true);
    this.errorEl?.setAttribute('hidden', '');

    try {
      const formData = new FormData(this.form);
      const res = await fetch(this.form.action, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'text/html' },
        redirect: 'follow'
      });

      /* Shopify redirects to ?contact_posted=true on success. The fetch
         follows the redirect and resolves with the resulting HTML, so we
         treat any 2xx response as success. */
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      this.showSuccess();
    } catch (err) {
      console.error('[lion-stock-notifier]', err);
      this.showError(this.dataset.errorMessage || 'Something went wrong. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    if (!this.submitBtn) return;
    this.submitBtn.disabled = isLoading;
    this.classList.toggle('is-loading', isLoading);
  }

  showSuccess() {
    this.successEl?.removeAttribute('hidden');
    this.form?.reset();
    this.classList.add('is-success');
    /* Hide the form after success (keep success message visible) */
    this.form?.querySelector('.stock-notifier__row')?.setAttribute('hidden', '');
  }

  showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.removeAttribute('hidden');
  }
}

if (!customElements.get('lion-stock-notifier')) {
  customElements.define('lion-stock-notifier', StockNotifier);
}
